import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ownerAPI from "../../../services/ownerApi";
import clubSupportAPI from "../../../services/clubSupportApi";
import platformAPI from "../../../services/platformApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { ReadTicks } from "../../Clinic/common/ChatThread";
import cs from "../../Clinic/common/chat.module.css";

const URL_RE = /(https?:\/\/[^\s<]+[^.,:;!?)\s]|www\.[^\s<]+[^.,:;!?)\s])/gi;
const ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip";
const POLL_MS = 2500;
const FULL_SYNC_EVERY = 2;

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Сегодня";
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function groupMessagesByDay(messages) {
  const groups = [];
  let currentDay = null;
  for (const msg of messages) {
    const day = formatDayLabel(msg.created_at);
    if (day !== currentDay) {
      currentDay = day;
      groups.push({ type: "day", key: `day-${day}-${msg.id}`, label: day });
    }
    groups.push({ type: "msg", key: msg.id, msg });
  }
  return groups;
}

function LinkifiedText({ text }) {
  const parts = String(text || "").split(URL_RE);
  return (
    <span className={cs.chatBubbleText}>
      {parts.map((part, i) => {
        if (!part) return null;
        if (/^(https?:\/\/|www\.)/i.test(part)) {
          const href = part.startsWith("http") ? part : `https://${part}`;
          return (
            <a
              key={`${part}-${i}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cs.chatBubbleLink}
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <span key={`${i}-${part.slice(0, 12)}`}>{part}</span>;
      })}
    </span>
  );
}

function SupportAttachment({ url, type, name }) {
  const src = mediaUrl(url);
  if (!src) return null;
  if (type === "video") {
    return (
      <video
        className={cs.chatBubbleVideo}
        src={src}
        controls
        playsInline
        preload="metadata"
      />
    );
  }
  if (type === "file") {
    return (
      <a
        className={cs.chatBubbleFile}
        href={src}
        target="_blank"
        rel="noopener noreferrer"
      >
        📎 {name || "Файл"}
      </a>
    );
  }
  return (
    <a
      className={cs.chatBubbleImageLink}
      href={src}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img className={cs.chatBubbleImage} src={src} alt="Фото" loading="lazy" />
    </a>
  );
}

function AttachIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l8.49-8.49a4 4 0 015.66 5.66l-8.49 8.49a2 2 0 01-2.83-2.83l7.07-7.07"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

async function fetchMessages(mode, threadKey, params) {
  if (mode === "owner") return ownerAPI.getSupportMessages(params);
  if (mode === "club") return clubSupportAPI.getSupportMessages(params);
  return platformAPI.getSupportThreadMessages(threadKey, params);
}

async function sendMessage(mode, threadKey, body) {
  if (mode === "owner") return ownerAPI.sendSupportMessage(body);
  if (mode === "club") return clubSupportAPI.sendSupportMessage(body);
  return platformAPI.sendSupportReply(threadKey, body);
}

async function uploadMedia(mode, file) {
  if (mode === "owner") return ownerAPI.uploadSupportMedia(file);
  if (mode === "club") return clubSupportAPI.uploadSupportMedia(file);
  return platformAPI.uploadSupportMedia(file);
}

export default function SupportChatThread({
  mode,
  threadKey = null,
  emptyHint = "Опишите вопрос — команда поддержки ответит здесь.",
  onMeta,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const sinceRef = useRef(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const pollCountRef = useRef(0);

  const mergeById = (prev, incoming) => {
    if (!incoming?.length) return prev;
    const map = new Map(prev.map((m) => [String(m.id), m]));
    for (const m of incoming) {
      const key = String(m.id);
      const existing = map.get(key);
      map.set(key, existing ? { ...existing, ...m } : m);
    }
    return [...map.values()].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
  };

  const load = useCallback(
    async ({ silent = false, markRead = true, full = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const params = {
          mark_read: markRead ? "1" : "0",
        };
        const useSince = silent && !full && sinceRef.current;
        if (useSince) params.since = sinceRef.current;

        const data = await fetchMessages(mode, threadKey, params);
        const incoming = data.messages || [];
        onMeta?.(data.meta || null);

        setMessages((prev) => {
          let next;
          if (!useSince) {
            next = full && silent ? mergeById(prev, incoming) : incoming;
          } else {
            next = mergeById(prev, incoming);
          }
          if (next.length) {
            sinceRef.current = next[next.length - 1].created_at;
          }
          return next;
        });
        setErr("");
      } catch (e) {
        if (!silent) setErr(e.userMessage || "Не удалось загрузить чат");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [mode, threadKey, onMeta],
  );

  useEffect(() => {
    sinceRef.current = null;
    pollCountRef.current = 0;
    setMessages([]);
    load({ silent: false, markRead: true, full: true });
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      pollCountRef.current += 1;
      const full = pollCountRef.current % FULL_SYNC_EVERY === 0;
      load({
        silent: true,
        markRead: document.hasFocus(),
        full,
      });
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, sending, uploading]);

  const grouped = useMemo(() => groupMessagesByDay(messages), [messages]);

  const pushMessage = (msg) => {
    if (!msg) return;
    setMessages((prev) => [...prev, msg]);
    sinceRef.current = msg.created_at;
    window.dispatchEvent(new CustomEvent("baysport:support-activity"));
    if (mode !== "platform") {
      window.dispatchEvent(new CustomEvent("baysport:platform-notify"));
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || sending || uploading) return;
    setSending(true);
    setErr("");
    try {
      const data = await sendMessage(mode, threadKey, { body });
      pushMessage(data.message);
      setText("");
    } catch (e2) {
      setErr(e2.userMessage || "Не удалось отправить");
    } finally {
      setSending(false);
    }
  };

  const onAttachPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || sending || uploading) return;
    setUploading(true);
    setErr("");
    try {
      const uploaded = await uploadMedia(mode, file);
      const data = await sendMessage(mode, threadKey, {
        body: text.trim(),
        attachment_url: uploaded.url,
        attachment_type: uploaded.attachment_type,
        attachment_name: uploaded.attachment_name || file.name,
      });
      pushMessage(data.message);
      setText("");
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.userMessage || "Не удалось прикрепить файл");
    } finally {
      setUploading(false);
    }
  };

  const canSend = Boolean(text.trim()) && !sending && !uploading;

  return (
    <div className={cs.chatWrap}>
      <div
        ref={scrollRef}
        className={`${cs.chatMessages} ${
          messages.length === 0 ? cs.chatMessagesEmpty : ""
        }`}
      >
        {loading ? (
          <div className={cs.chatLoading}>
            <div className={cs.chatLoadingDots} aria-hidden>
              <span />
              <span />
              <span />
            </div>
            Загрузка…
          </div>
        ) : messages.length === 0 ? (
          <div className={cs.chatEmpty}>
            <div className={cs.chatEmptyIcon} aria-hidden>
              💬
            </div>
            <p className={cs.chatEmptyTitle}>Чат с поддержкой</p>
            <p className={cs.chatEmptyHint}>{emptyHint}</p>
          </div>
        ) : (
          grouped.map((item) => {
            if (item.type === "day") {
              return (
                <div key={item.key} className={cs.chatDaySep}>
                  {item.label}
                </div>
              );
            }
            const m = item.msg;
            const mine = m.is_mine;
            const hasMedia = Boolean(m.attachment_url);
            return (
              <div
                key={item.key}
                className={`${cs.chatRow} ${mine ? cs.chatRowMine : cs.chatRowTheirs}`}
              >
                <div className={cs.chatBubbleCol}>
                  <div
                    className={`${cs.chatBubble} ${
                      mine ? cs.chatBubbleMine : cs.chatBubbleTheirs
                    } ${hasMedia ? cs.chatBubbleHasMedia : ""}`}
                  >
                    {!mine ? (
                      <span className={cs.chatBubbleName}>{m.sender_label}</span>
                    ) : null}
                    {hasMedia ? (
                      <SupportAttachment
                        url={m.attachment_url}
                        type={m.attachment_type}
                        name={m.attachment_name}
                      />
                    ) : null}
                    {m.body ? <LinkifiedText text={m.body} /> : null}
                    <span className={cs.chatBubbleFooter}>
                      <span className={cs.chatBubbleTime}>
                        {formatTime(m.created_at)}
                      </span>
                      {mine ? (
                        <ReadTicks
                          read={Boolean(m.is_read ?? m.read)}
                        />
                      ) : null}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {err ? (
        <p
          className={cs.chatEmptyHint}
          style={{ padding: "8px 16px", color: "var(--color-danger)" }}
        >
          {err}
        </p>
      ) : null}

      <form className={cs.chatComposer} onSubmit={send}>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className={cs.chatFileInput}
          onChange={onAttachPick}
          aria-hidden
          tabIndex={-1}
        />
        <button
          type="button"
          className={cs.chatAttachBtn}
          onClick={() => fileRef.current?.click()}
          disabled={sending || uploading}
          aria-label="Прикрепить фото, видео или файл"
        >
          <AttachIcon />
        </button>
        <textarea
          className={cs.chatInput}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(e);
            }
          }}
          placeholder={uploading ? "Загрузка файла…" : "Сообщение"}
          disabled={sending || uploading}
        />
        <button
          type="submit"
          className={cs.chatSendBtn}
          disabled={!canSend}
          aria-label="Отправить"
        >
          ↑
        </button>
      </form>
    </div>
  );
}
